import React, { lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { Loader } from "../components/Loader";
import RequireAuth from "./auth/RequireAuth";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useDispatch } from "react-redux";
import { setRemoteProperty } from "../redux/features/main/remoteConfigSlice";

// Main
const Main = lazy(() => import("./main/index"));
const Home = lazy(() => import("./main/contents/Home/index"));
const LearningJourney = lazy(() => import("./main/contents/LearningJourney/index"));

const Help = lazy(() => import("./main/contents/Help/index"));
const HelpPublic = lazy(() => import("./main/contents/HelpPublic/index"));

const Leaderboards = lazy(() => import("./main/contents/Leaderboards/index"));
const Reviews = lazy(() => import("./main/contents/Reviews/index"));
const Rewards = lazy(() => import("./main/contents/Rewards/index"));
const MembersProfile = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesMobile/TeamLearningStatusDetail/contents/MembersProfile/index")
);
const MembersProfileDesktop = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesDesktop/TeamLearningStatusDetailDesktop/contents/MembersProfileDesktop/index")
);
const ContentLibrary = lazy(() => import("./main/contents/ContentLibrary/index"));
const Academy = lazy(() => import("./main/contents/ContentLibrary/pages/Academy/index"));
const TeamMonitoring = lazy(() => import("./main/contents/TeamMonitoring/index"));
const LearningPages = lazy(() => import("./main/contents/LearningJourney/LearningPages/index"));
const Journey = lazy(() =>
  import("./main/contents/LearningJourney/LearningPages/SubPages/Journey/index")
);
import Course from "./main/contents/LearningJourney/LearningPages/SubPages/Course/index";
import Module from "./main/contents/LearningJourney/LearningPages/SubPages/Module/index";
import StreamSCORM from "./main/contents/LearningJourney/LearningPages/SubPages/Module/StreamSCORM/index";

const LearningLibraryPages = lazy(() =>
  import("./main/contents/ContentLibrary/pages/LearningLibraryPages/index")
);
const JourneyCL = lazy(() =>
  import("./main/contents/ContentLibrary/pages/LearningLibraryPages/SubPages/Journey/index")
);
import CourseCL from "./main/contents/ContentLibrary/pages/LearningLibraryPages/SubPages/CourseCL/index";
import ModuleCL from "./main/contents/ContentLibrary/pages/LearningLibraryPages/SubPages/ModuleCL/index";
import StreamSCORMCL from "./main/contents/ContentLibrary/pages/LearningLibraryPages/SubPages/ModuleCL/StreamSCORMCL/index";

const Profile = lazy(() => import("./main/contents/Profile/index"));
import HelpFaQ from "./main/contents/Help/elements/faq/index";
import HelpLogin from "./main/contents/Help/elements/login/index";
import HelpProfileContact from "./main/contents/Help/elements/profile&contacts/index";
import HelpMyLearningJourney from "./main/contents/Help/elements/mylearningjourney";
import HelpLearningActivity from "./main/contents/Help/elements/learningactivity/index";
import HelpLearningPoint from "./main/contents/Help/elements/learningpoint/index";
import HelpSupervisorReviewer from "./main/contents/Help/elements/supervisor&reviewer/index";
import HelpDataSecurity from "./main/contents/Help/elements/accountdata&security/index";
import HelpOthers from "./main/contents/Help/elements/others/index";
import HelpTermOfService from "./main/contents/Help/elements/termofservice/index";
import HelpPrivacyPolicy from "./main/contents/Help/elements/privacypolicy/index";

import HelpPublicFaQ from "./main/contents/HelpPublic/elements/faq/index";
import HelpPublicLogin from "./main/contents/HelpPublic/elements/login/index";
import HelpPublicProfileContact from "./main/contents/HelpPublic/elements/profile&contacts/index";
import HelpPublicMyLearningJourney from "./main/contents/HelpPublic/elements/mylearningjourney";
import HelpPublicLearningActivity from "./main/contents/HelpPublic/elements/learningactivity/index";
import HelpPublicLearningPoint from "./main/contents/HelpPublic/elements/learningpoint/index";
import HelpPublicSupervisorReviewer from "./main/contents/HelpPublic/elements/supervisor&reviewer/index";
import HelpPublicDataSecurity from "./main/contents/HelpPublic/elements/accountdata&security/index";
import HelpPublicOthers from "./main/contents/HelpPublic/elements/others/index";
import HelpPublicTermOfService from "./main/contents/HelpPublic/elements/termofservice/index";
import HelpPublicPrivacyPolicy from "./main/contents/HelpPublic/elements/privacypolicy/index";

import Scanner from './main/contents/Scanner/index';
import ListAttendance from './main/contents/Scanner/ListAttendance/index';

const SelectedPrograms = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesMobile/SelectedPrograms/index")
);
const SelectedProgramsDesktop = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesDesktop/SelectedProgramsDesktop/index")
);
const DashboardTeam = lazy(() =>
  import("./main/contents/TeamMonitoring/elements/Sections/SectionsMobile/DashboardTeam")
);
const DashboardTeamDesktop = lazy(() =>
  import("./main/contents/TeamMonitoring/elements/Sections/SectionsDesktop/DashboardTeamDesktop")
);
const TeamLearningStatusDetail = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesMobile/TeamLearningStatusDetail/index")
);
const TeamLearningStatusDetailDesktop = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesDesktop/TeamLearningStatusDetailDesktop/index")
);
const RewardHistory = lazy(() =>
  import("./main/contents/Rewards/page/RewardHistory/index")
);
const CalendarEvent = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesMobile/CalendarEvent/index")
);
const CalendarEventDesktop = lazy(() =>
  import("./main/contents/TeamMonitoring/pages/pagesDesktop/CalendarEventDesktop/index")
);

// Auth
const LoginMain = lazy(() => import("./auth/LoginMain"));
const Login = lazy(() => import("./auth/Login"));
const ForgotPassword = lazy(() => import("./auth/ForgotPassword"));
const NotFound = lazy(() => import("./auth/NotFound"));
const ChangePassword = lazy(() => import("./auth/ChangePassword"));
const TemporaryPassword = lazy(() =>
  import("./auth/TemporaryPassword/TemporaryPassword")
);
const AutoLogin = lazy(() => import("./auth/AutoLogin"));

import DoubleButton from "./main/DoubleButton";

import RemoteConfig from "./../config/remote_config.json"


// prevent browser to fired default Modal Confirmation
window.addEventListener("beforeunload", function (event) {
  event.stopImmediatePropagation();
});

function App() {
  const screens = useBreakpoint();
  const isScallingVersion =
  window.innerWidth < 992 && (window.innerHeight >= window.innerWidth || window.innerWidth > window.innerHeight);

  const isMobileVersion = screens.xs || isScallingVersion;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setRemoteProperty(RemoteConfig));
  },[])

  useEffect(() => {
    const protectionEnabled =
      import.meta.env.VITE_DISABLE_RIGHT_CLICK === "true";

    if (!protectionEnabled) return;

    const handleContextMenu = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
      const blocked =
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i");

      if (blocked) e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);


  return (
      <Routes>
        {/* public routes */}
        <Route
          path={`/`}
          element={
          <React.Suspense fallback={<Loader />}>
            {/* <Login isMobileVersion={isMobileVersion} /> */}
            <LoginMain />
          </React.Suspense>}
        />
        <Route
          path={`login`}
          element={
          <React.Suspense fallback={<Loader />}>
            {/* <Login isMobileVersion={isMobileVersion} /> */}
            <LoginMain />
          </React.Suspense>}
        />
        <Route path={`forgot-password`} element={
          <React.Suspense fallback={<Loader />}>
            <ForgotPassword />
          </React.Suspense>
          } />
        <Route path={`change-password`} element={<React.Suspense fallback={<Loader />}><ChangePassword /></React.Suspense>} />
        <Route path={`temporary-password`} element={<React.Suspense fallback={<Loader />}><TemporaryPassword /></React.Suspense>} />
        <Route path={`/auto-login`} element={<React.Suspense fallback={<Loader />}><AutoLogin /></React.Suspense>} />

        {/* protected routes */}
        <Route element={<RequireAuth />}>
          <Route element={<Main isMobileVersion={isMobileVersion} />}>
            <Route
              path={`/home`}
              element={
                <React.Suspense fallback={<Loader />}>
                  <Home isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
            />
            <Route
              path={`/my-learning-journey`}
              element={<React.Suspense fallback={<Loader />}><LearningJourney isMobileVersion={isMobileVersion} /></React.Suspense>}
            />
            <Route
              path={`/leaderboards`}
              element={<React.Suspense fallback={<Loader />}><Leaderboards isMobileVersion={isMobileVersion} /></React.Suspense>}
            />
            <Route
              path={`/reviews`}
              element={<React.Suspense fallback={<Loader />}><Reviews isMobileVersion={isMobileVersion} /></React.Suspense>}
            />
            <Route
              path={`/help`}
              element={<React.Suspense fallback={<Loader />}><Help isMobileVersion={isMobileVersion} /></React.Suspense>}
            />
            <Route path={`/profile`} element={<React.Suspense fallback={<Loader />}><Profile /></React.Suspense>} />

            {/* scanner attendance */}
              <Route path={`/scan`} element={<Scanner />} />
              <Route path={`/scan/:id/attendance`} element={<ListAttendance />} />

            {/* Content Library routes */}
            <Route
              path={`/content-library`}
              element={<React.Suspense fallback={<Loader />}><ContentLibrary isMobileVersion={isMobileVersion} /></React.Suspense>}
            />
            <Route
              path={`/content-library/academy/:id`}
              element={<React.Suspense fallback={<Loader />}><Academy isMobileVersion={isMobileVersion} /></React.Suspense>}
            />

            {/* Searching */}

            {/* sub routes */}
            <Route
              element={<React.Suspense fallback={<Loader />}><LearningPages isMobileVersion={isMobileVersion} /></React.Suspense>}
            >
              <Route
                path={`/my-learning-journey/journey/:id`}
                element={<React.Suspense fallback={<Loader />}><Journey isMobileVersion={isMobileVersion} /></React.Suspense>}
              />
              <Route
                path={`/my-learning-journey/journey/:id/course/:id`}
                element={<Course isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/my-learning-journey/journey/:id/course/:id/module/:id`}
                element={<Module isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/my-learning-journey/journey/:id/course/:id/module/:id/play`}
                element={<StreamSCORM isMobileVersion={isMobileVersion} />}
              />
              {/* supporting routes */}
              <Route
                path={`/my-learning-journey/journey/:id/course/:id/supporting/:id`}
                element={<Module isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/my-learning-journey/journey/:id/course/:id/supporting/:id/play`}
                element={<StreamSCORM isMobileVersion={isMobileVersion} />}
              />
            </Route>

            {/* sub routes content library learning pages */}
            <Route
              element={
                <React.Suspense fallback={<Loader />}>
                  <LearningLibraryPages isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
            >
              <Route
                path={`/content-library/academy/:id/journey/:id`}
                element={
                  <React.Suspense fallback={<Loader />}>
                    <JourneyCL isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                    }
              />
              <Route
                path={`/content-library/academy/:id/journey/:id/course/:id`}
                element={<CourseCL isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/content-library/academy/:id/journey/:id/course/:id/module/:id`}
                element={<ModuleCL isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/content-library/academy/:id/journey/:id/course/:id/module/:id/play`}
                element={<StreamSCORMCL isMobileVersion={isMobileVersion} />}
              />
              {/* supporting routes */}
              <Route
                path={`/content-library/academy/:id/journey/:id/course/:id/supporting/:id`}
                element={<ModuleCL isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/content-library/academy/:id/journey/:id/course/:id/supporting/:id/play`}
                element={<StreamSCORMCL isMobileVersion={isMobileVersion} />}
              />
            </Route>

            <Route
              element={
                <React.Suspense fallback={<Loader />}>
                  <TeamMonitoring
                    isMobileVersion={isMobileVersion}
                    isScallingVersion={isScallingVersion}
                  />
                </React.Suspense>
              }
            >
              <Route
                path={`/team-monitoring`}
                element={isMobileVersion ? <React.Suspense fallback={<Loader />}><DashboardTeam isMobileVersion={isMobileVersion} /></React.Suspense> : <React.Suspense fallback={<Loader />}><DashboardTeamDesktop isMobileVersion={isMobileVersion} /></React.Suspense>}
              />
              <Route
                path={`/team-monitoring/journey/:id`}
                element={
                  isMobileVersion ?
                  <React.Suspense fallback={<Loader />}>
                    <SelectedPrograms
                      isMobileVersion={isMobileVersion}
                      isScallingVersion={isScallingVersion}
                    />
                  </React.Suspense>
                  :
                  <React.Suspense fallback={<Loader />}>
                    <SelectedProgramsDesktop isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                }
              />
              <Route
                path={`/team-monitoring/learning-status/detail`}
                element={
                  isMobileVersion ?
                  <React.Suspense fallback={<Loader />}>
                    <TeamLearningStatusDetail
                      isMobileVersion={isMobileVersion}
                      isScallingVersion={isScallingVersion}
                    />
                  </React.Suspense>
                  :
                  <React.Suspense fallback={<Loader />}>
                    <TeamLearningStatusDetailDesktop isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                }
              />
              <Route
                path={`/team-monitoring/team-profile`}
                element={
                  isMobileVersion ?
                  <React.Suspense fallback={<Loader />}>
                    <TeamLearningStatusDetail isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                  :
                  <React.Suspense fallback={<Loader />}>
                    <TeamLearningStatusDetailDesktop isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                }
              />
              <Route
                path={`/team-monitoring/event/detail`}
                element={
                  isMobileVersion?
                  <React.Suspense fallback={<Loader />}>
                    <CalendarEvent isMobileVersion={isMobileVersion} />
                  </React.Suspense>
                :
                <React.Suspense fallback={<Loader />}>
                  <CalendarEventDesktop isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
              />
            </Route>

            <Route
              path={`/team-monitoring/member/profile/:id`}
              element={
                isMobileVersion ?
                <React.Suspense fallback={<Loader />}>
                  <MembersProfile
                    isMobileVersion={isMobileVersion}
                    isScallingVersion={isScallingVersion}
                  />
                </React.Suspense>
                :
                <React.Suspense fallback={<Loader />}>
                  <MembersProfileDesktop isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
            />

            <Route element={
              <React.Suspense fallback={<Loader />}>
                <Help isMobileVersion={isMobileVersion} />
              </React.Suspense>
            }>
              <Route
                path={`/help/faq`}
                element={<HelpFaQ isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/login`}
                element={<HelpLogin isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/profile`}
                element={
                  <HelpProfileContact isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/mylearningjourney`}
                element={
                  <HelpMyLearningJourney isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/learningactivity`}
                element={
                  <HelpLearningActivity isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/learningpoint`}
                element={
                  <HelpLearningPoint isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/supervisorreviewer`}
                element={
                  <HelpSupervisorReviewer isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/datasecurity`}
                element={<HelpDataSecurity isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/others`}
                element={<HelpOthers isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/termofservice`}
                element={
                  <HelpTermOfService isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/privacypolicy`}
                element={
                  <HelpPrivacyPolicy isMobileVersion={isMobileVersion} />
                }
              />
            </Route>

            <Route
              path={`/reviews/module/:id/:id`}
              element={
                <React.Suspense fallback={<Loader />}>
                  <Reviews isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
            />

            <Route
              path={`/rewards`}
              element={
                <React.Suspense fallback={<Loader />}>
                  <Rewards
                    isMobileVersion={isMobileVersion}
                    isScallingVersion={isScallingVersion}
                  />
                </React.Suspense>
              }
            />
            <Route
              path={`/rewards/history`}
              element={
                <React.Suspense fallback={<Loader />}>
                  <RewardHistory isMobileVersion={isMobileVersion} />
                </React.Suspense>
              }
            />
          </Route>
        </Route>

        {/* public help route */}
        <Route
            path={`/help/public`}
            element={<HelpPublic isMobileVersion={isMobileVersion} />}
        />
        <Route element={<HelpPublic isMobileVersion={isMobileVersion} />}>
          <Route
                path={`/help/public/faq`}
                element={<HelpPublicFaQ isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/public/login`}
                element={<HelpPublicLogin isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/public/profile`}
                element={
                  <HelpPublicProfileContact isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/mylearningjourney`}
                element={
                  <HelpPublicMyLearningJourney isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/learningactivity`}
                element={
                  <HelpPublicLearningActivity isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/learningpoint`}
                element={
                  <HelpPublicLearningPoint isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/supervisorreviewer`}
                element={
                  <HelpPublicSupervisorReviewer isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/datasecurity`}
                element={<HelpPublicDataSecurity isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/public/others`}
                element={<HelpPublicOthers isMobileVersion={isMobileVersion} />}
              />
              <Route
                path={`/help/public/termofservice`}
                element={
                  <HelpPublicTermOfService isMobileVersion={isMobileVersion} />
                }
              />
              <Route
                path={`/help/public/privacypolicy`}
                element={
                  <HelpPublicPrivacyPolicy isMobileVersion={isMobileVersion} />
                }
              />
        </Route>

        <Route
          path={`/landing-page-sso`}
          element={<DoubleButton />}
        />

        {/* catch all */}
        <Route path={`*`} element={
          <React.Suspense fallback={<Loader />}>
            <NotFound />
          </React.Suspense>
        } />
      </Routes>
  );
}

export default App;
